 
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CREATED,
  PAUSED,
  PENDING,
  RUNNING,
} from '@whistler-ui/portal/constants/testConstants';
import {
  createTestsApiExecution,
  createTestsUsecaseExecution,
  getTestsApiExecution,
  getTestsUsecaseExecutionById,
} from '@whistler-ui/portal/services/testsExecutionService';
import {
  ApiTestExecution,
  TCreateApiTestExecutionPayload,
  TCreateUseCaseTestExecutionPayload,
  UseCaseTestExecution,
} from '@whistler-ui/portal/types/tests';
import React from 'react';

type TCountRef = { [key: string]: number };

const getTimeout = (
  countRef: React.MutableRefObject<TCountRef>,
  id: string,
) => {
  return Math.min(2000, Math.pow(2, countRef.current?.[id]) * (1000 * 0.05));
};

export const useStartApiTestExecution = () => {
  return useMutation<ApiTestExecution, Error, TCreateApiTestExecutionPayload>({
    mutationKey: ['start_api_test_execution'],
    mutationFn: createTestsApiExecution,
  });
};

export const useGetApiTestExecution = (
  id: string | any,
  isEnabled: boolean,
  resumed: boolean,
) => {
  const countRef = React.useRef<TCountRef>({});

  return useQuery<ApiTestExecution>({
    queryKey: ['get_api_test_execution', id],
    queryFn: () => getTestsApiExecution(id),
    enabled: Boolean(id) && isEnabled,
    refetchInterval(query) {
      if (query?.state?.data) {
        const execution = query.state.data;

        if (!execution || !execution.status) {
          return undefined;
        }

        const { id } = execution;
        const statusList = [CREATED, RUNNING, PENDING];

        if (resumed) {
          statusList.push(PAUSED);
        }

        if (statusList.includes(execution.status)) {
          if (!countRef.current) {
            countRef.current = {};
          }

          countRef.current[id] = query.state.dataUpdateCount;

          return getTimeout(countRef, id);
        }

        delete countRef.current[id];
      }

      return undefined;
    },
  });
};

export const useStartUseCaseTestExecution = () => {
  return useMutation<
    UseCaseTestExecution,
    Error,
    TCreateUseCaseTestExecutionPayload
  >({
    mutationKey: ['start_use_case_test_execution'],
    mutationFn: createTestsUsecaseExecution,
  });
};

export const useGetUseCaseTestsExecution = (
  id: string | unknown,
  isEnabled: boolean,
  resumed: boolean,
) => {
  const countRef = React.useRef<TCountRef>({});

  return useQuery<UseCaseTestExecution>({
    queryKey: ['get_use_case_tests_execution', id],
    queryFn: () => getTestsUsecaseExecutionById(id),
    retry: 1,
    enabled: Boolean(id) && isEnabled,
    refetchInterval(query) {
      if (query?.state?.data) {
        const execution = query.state.data;

        if (!execution || !execution.status) {
          return undefined;
        }

        const { id } = execution;
        const statusList = [CREATED, RUNNING, PENDING];

        if (resumed) {
          statusList.push(PAUSED);
        }

        if (statusList.includes(execution.status)) {
          if (!countRef.current) {
            countRef.current = {}; // Ensure it's initialized
          }

          countRef.current[id] = query.state.dataUpdateCount;

          return getTimeout(countRef, id);
        }

        delete countRef.current[id];
      }

      return undefined;
    },
  });
};
